import { markRaw, toRaw, type Ref } from "vue";

/**
 * Marks every row of a read-only catalog raw and returns the same array, ready to assign to the store's
 * `shallowRef`. The catalogs (tickers, sectors, authors, categories, products) are replaced wholesale and never
 * edited row by row, so deep proxies bought nothing: they made every lookup and every hydration pass walk the
 * rows through Vue's proxy traps, and let any reactive holder of a row (an article's `tickers`) re-wrap it.
 */
export function markRawRows< T extends object >( rows: T[] ): T[] {
    for ( const row of rows ) {
        markRaw( row );
    }
    return rows;
}

export interface CatalogIndex< K, T > {
    /** The first row whose key matches, as `Array.prototype.find` would return it. */
    get( key: K ): T | undefined;
    /** Drops the Map; the next `get` rebuilds it from the catalog. Called from the store's reset(). */
    clear(): void;
}

/**
 * A Map index over one field of a catalog held in a ref (rank 11: content hydration resolved every ticker symbol
 * with a linear `.find` over the deep-reactive ticker universe, ~0.9 s for 3,000 lookups against 5k tickers).
 *
 * The Map is rebuilt lazily whenever the ref holds a different array, or the same array at a different length, so
 * a store only has to assign its catalog. `get` reads `source.value` first, which keeps it a reactive dependency:
 * a computed that looked a row up before the catalog landed still re-runs once it is assigned.
 */
export function createCatalogIndex< T, K >(
    source: Readonly< Ref< readonly T[] > >,
    keyOf: ( row: T ) => K | null | undefined
): CatalogIndex< K, T > {
    let indexedRows: readonly T[] | undefined;
    let indexedLength: number = -1;
    let byKey = new Map< K, T >();

    function rebuild( rows: readonly T[] ): void {
        byKey = new Map< K, T >();
        for ( const row of rows ) {
            const key = keyOf( row );
            // Keep the first row per key, matching the `.find` lookups this replaces.
            if ( key !== null && key !== undefined && !byKey.has( key ) ) {
                byKey.set( key, row );
            }
        }
        indexedRows = rows;
        indexedLength = rows.length;
    }

    return {
        get( key: K ): T | undefined {
            const rows = toRaw( source.value );
            if ( rows !== indexedRows || rows.length !== indexedLength ) {
                rebuild( rows );
            }
            return byKey.get( key );
        },
        clear(): void {
            byKey = new Map< K, T >();
            indexedRows = undefined;
            indexedLength = -1;
        }
    };
}
